import { createColumnHelper } from "@tanstack/react-table";
import clsx from "clsx";

import CompanyLogo from "~/components/CompanyLogo";
import Tooltip from "~/components/Tooltip";
import { EMPTY_VALUE_PLACEHOLDER } from "~/constants/textConstants.ts";
import { getShortInvoiceTypeDisplayNameById } from "~/modules/billing/api/invoiceType.ts";
import type { InvoicesIndexTableData } from "~/modules/billing/components/InvoicesIndexView/InvoicesIndexTableTypes.ts";
import { getInvoiceStatusIcon } from "~/modules/billing/components/InvoicesIndexView/invoicesIndexUtils.tsx";
import { sortByInvoiceNumber } from "~/modules/billing/utils/billingUtils.ts";
import { formatNumberToCurrency } from "~/utils/currencyUtils.ts";
import { formatDateCompact, formatSmartDateRange } from "~/utils/dateAndTimeUtils.ts";
import { customArrayIncludesSome } from "~/utils/tanStackTableUtils.ts";

const columnHelper = createColumnHelper<InvoicesIndexTableData>();

export const invoicesIndexTableColumnDefs = [
	columnHelper.accessor("invoiceStatusId", {
		header: "Status",
		meta: {
			cellTextAlign: "center",
			width: "4rem",
		},
		cell: (props) => {
			const statusId = props.getValue();
			const { isOverdue } = props.row.original;
			return <div className="flex items-center justify-center">{ getInvoiceStatusIcon(statusId, "size-6", isOverdue)}</div>
		},
		filterFn:customArrayIncludesSome,
		sortingFn: (rowA, rowB) =>
			rowA.original.invoiceStatusOrder - rowB.original.invoiceStatusOrder,
	}),
	columnHelper.accessor("invoiceTypeId", {
		header: "Art",
		meta: {
			cellTextAlign: "center",
			width: "9rem",
		},
		cell: (props) => {
			return getShortInvoiceTypeDisplayNameById(props.getValue()) || EMPTY_VALUE_PLACEHOLDER;
		},

	}),
	columnHelper.accessor("companyShortName", {
		header: "Kunde",
		meta: {
			cellTextAlign: "center",
			width: "6rem",
		},
		cell: (props) => {
			const companyShortName = props.getValue();
			const rowData = props.row.original;
			const isProjectInvoice = props.row.original.isProjectInvoice;
			if (!isProjectInvoice) return EMPTY_VALUE_PLACEHOLDER;
			return <div className="flex items-center gap-x-2">
				{rowData.companyAvatarImage && <CompanyLogo image={rowData.companyAvatarImage}
															sizeClassName="size-6" />}
				<span>{companyShortName}</span>
			</div>;
		},
		filterFn:(row, _, value) => {
			if (!Array.isArray(value)) {
				return false;
			}

			if (value.length === 0) {
				return true;
			}
			return value.includes(row.original.companyId);
		},
	}),
	columnHelper.accessor("invoiceNumber", {
		header: "Rechnungsnummer",
		meta: {
			headerTextAlign: "left",
			cellTextAlign: "left",
			width: "10rem",
		},
		cell: (props) => props.getValue() || EMPTY_VALUE_PLACEHOLDER,
		sortingFn: sortByInvoiceNumber,
	}),
	columnHelper.accessor("totalAmountCents", {
		header: () => <div>Rechnungsbetrag<br />(brutto)</div>,
		meta: {
			headerTextAlign: "center",
			cellTextAlign: "right",
			width: "10rem",
		},
		cell: (props) => {
			const rawValue = props.getValue();
			const formattedValue = props.getValue() ? formatNumberToCurrency(props.getValue()) + " " + props.row.original.currencySymbol : EMPTY_VALUE_PLACEHOLDER;

			return <div className={clsx(rawValue < 0 && "text-secondary-500", "text-right")}>{formattedValue}</div>;
		},
		footer: (props) => {
			const total = props.table.getFilteredRowModel().rows.reduce((sum, row) => {
				return sum + (row.original.totalAmountCents || 0);
			}, 0);
			return <div className="flex w-full items-center justify-between text-sm font-bold">
				<span>&sum;</span><span>{formatNumberToCurrency(total)} €</span></div>;
		},
	}),
	columnHelper.accessor("deliveryStartDate", {
		header: "Leistungszeitraum",
		meta: {
			headerTextAlign: "center",
			cellTextAlign: "center",
			width: "10rem",
		},
		cell: (props) => {
			const rowData = props.row.original;
			if (!rowData.deliveryStartDate || !rowData.deliveryEndDate) return EMPTY_VALUE_PLACEHOLDER;
			return formatSmartDateRange(rowData.deliveryStartDate, rowData.deliveryEndDate, {
				month: "MMM",
				year: " yy",
			});
		},
	}),
	columnHelper.accessor("paymentDueAt", {
		header: "Fällig am",
		meta: {
			headerTextAlign: "center",
			cellTextAlign: "center",
			width: "8rem",
		},
		cell: (props) => {
			const date = props.getValue();
			if (!date) return EMPTY_VALUE_PLACEHOLDER;
			const { isOverdue } = props.row.original;
			return <span className={clsx(isOverdue && "font-bold text-secondary-700")}>{formatDateCompact(date, " yyyy")}</span>;
		},
		filterFn: customArrayIncludesSome,
	}),
	columnHelper.accessor("updatedAt", {
		header: "Bearbeitet am",
		meta: {
			headerTextAlign: "center",
			cellTextAlign: "center",
			width: "8rem",
		},
		cell: (props) => {
			const date = props.getValue();
			if (!date) return EMPTY_VALUE_PLACEHOLDER;
			return formatDateCompact(date, " yyyy");
		},
	}),
	columnHelper.accessor("comments", {
		header: "Bemerkungen",
		meta: {
			headerTextAlign: "center",
			cellTextAlign: "left",
		},
		cell: (props) => {
			const comments = props.getValue();
			if (!comments) return <div className="flex justify-center">{EMPTY_VALUE_PLACEHOLDER}</div>;
			return <Tooltip text={comments}
							className="max-w-96 whitespace-pre-wrap break-words">
				<div className="truncate">{comments}</div>
			</Tooltip>;
		},
	}),

];